// frontend/src/pages/_LeftNav.jsx
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { canManage } from "../utils/roles";
import { SECTIONS, visibleNavItems } from "./nav/navItems";
import { saveNavPins } from "../api";
import { MAX_PINS, sanitizePins, togglePin } from "./nav/navPins";

const linkStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 12px",
  borderRadius: 10,
  textDecoration: "none",
  color: "#111827",
  fontWeight: 500,
  transition: "all 0.2s ease",
};

const activeStyle = { background: "#e5e7eb", fontWeight: 700 };

const iconStyle = {
  fontSize: 18,
  minWidth: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export default function LeftNav({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem("leftNavCollapsed") === "true";
  });
  const [lastClientId, setLastClientId] = useState(
    sessionStorage.getItem("lastViewedClientId")
  );

  useEffect(() => {
    localStorage.setItem("leftNavCollapsed", isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    if (location.pathname === "/clients") {
      sessionStorage.setItem("lastClientsView", "list");
      sessionStorage.removeItem("lastViewedClientId");
      setLastClientId(null);
    } else if (location.pathname.startsWith("/clients/")) {
      const clientId = location.pathname.split("/")[2];
      sessionStorage.setItem("lastClientsView", "detail");
      sessionStorage.setItem("lastViewedClientId", clientId);
      setLastClientId(clientId);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => {
      const id = sessionStorage.getItem("lastViewedClientId");
      setLastClientId(id);
    };
    window.addEventListener("last-client-changed", handler);
    return () => window.removeEventListener("last-client-changed", handler);
  }, []);

  const handleClientsClick = (e) => {
    e.preventDefault();
    const lastView = sessionStorage.getItem("lastClientsView");
    const storedClientId = sessionStorage.getItem("lastViewedClientId");
    if (lastView === "detail" && storedClientId) {
      navigate(`/clients/${storedClientId}`);
    } else {
      navigate("/clients");
    }
  };

  const visible = visibleNavItems(user);
  const visibleIds = visible.map((i) => i.id);
  const [pins, setPins] = useState(() => sanitizePins(user?.pins, visibleIds));

  // `user` arrives after the first render (App fetches `me` at load), so the
  // initial state above sees no pins. Re-sanitise when it lands -- against
  // THIS user's visible items, so a pin for a page they may not open is
  // dropped rather than rendered as a link that will turn them away.
  useEffect(() => {
    setPins(sanitizePins(user?.pins, visibleNavItems(user).map((i) => i.id)));
  }, [user]);

  const [pinError, setPinError] = useState("");

  const handleTogglePin = (id) => {
    const next = togglePin(pins, id);
    if (next === pins) {
      setPinError(`Up to ${MAX_PINS} pins.`);
      return;
    }
    // Whether THIS call is adding or removing `id` -- not a snapshot of the
    // whole list, which a second in-flight toggle can move out from under
    // us by the time this one's save settles.
    const wasAdding = !pins.includes(id);
    setPins(next);
    setPinError("");
    saveNavPins(next).catch(() => {
      // Undo exactly this call's effect against the CURRENT state, rather
      // than restoring a snapshot of the past -- a snapshot is only correct
      // if nothing else changed in between, and a second toggle landing
      // before this one fails is exactly the case where something did.
      setPins((current) => {
        if (wasAdding) return current.filter((p) => p !== id);
        if (current.includes(id)) return current;
        // Putting it back would exceed the cap the server enforces; leave
        // it off and let the error message stand instead.
        if (current.length >= MAX_PINS) return current;
        return [...current, id];
      });
      setPinError("Could not save that pin.");
    });
  };

  // Hover and focus are tracked separately: a pointer leaving the row must
  // not hide a star the keyboard is still sitting on.
  const [hoveredId, setHoveredId] = useState(null);
  const [focusedId, setFocusedId] = useState(null);

  // A row is a flex container holding the link and its star as SIBLINGS. The
  // star used to live inside the <a>: a <button> is interactive content and
  // is not valid there, and a link's accessible name is computed from its
  // descendants -- so every link announced as "Invoices Pin Invoices, link",
  // with the verb flipping between Pin and Unpin as the pin state changed.
  const renderItem = (item) => {
    const isPinned = pins.includes(item.id);
    // The spec: "Hovering an item reveals a star". Focus reveals it too --
    // otherwise the only way to find the control is with a mouse.
    const starShown =
      isPinned || hoveredId === item.id || focusedId === item.id;
    return (
      <div
        key={item.id}
        data-testid={`nav-row-${item.id}`}
        style={{ position: "relative", display: "flex", alignItems: "center" }}
        onMouseEnter={() => setHoveredId(item.id)}
        onMouseLeave={() =>
          setHoveredId((cur) => (cur === item.id ? null : cur))
        }
      >
        <NavLink
          to={item.path}
          onClick={item.id === "clients" ? handleClientsClick : undefined}
          style={({ isActive }) => ({
            ...linkStyle,
            ...(isActive ? activeStyle : {}),
            justifyContent: isCollapsed ? "center" : "flex-start",
            flex: 1,
            minWidth: 0,
            // Room for the star, which overlays the link's right edge so the
            // active background still runs the full width of the row.
            paddingRight: isCollapsed ? 12 : 30,
          })}
          title={isCollapsed ? item.label : ""}
        >
          <span style={iconStyle}>{item.icon}</span>
          {!isCollapsed && <span style={{ flex: 1 }}>{item.label}</span>}
        </NavLink>
        {!isCollapsed && (
          <button
            type="button"
            data-testid={`pin-toggle-${item.id}`}
            aria-label={isPinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
            onFocus={() => setFocusedId(item.id)}
            onBlur={() =>
              setFocusedId((cur) => (cur === item.id ? null : cur))
            }
            onClick={(e) => {
              // The star overlays the link's right edge; keep the guard so a
              // click on it can never navigate.
              e.preventDefault();
              e.stopPropagation();
              handleTogglePin(item.id);
            }}
            style={{
              position: "absolute", right: 8,
              background: "none", border: "none", cursor: "pointer",
              fontSize: 13, lineHeight: 1, padding: 2,
              color: isPinned ? "#b45309" : "#d1d5db",
              // opacity, not visibility or a conditional render: those take
              // the button out of the tab order, and a control you can only
              // reach with a mouse is not reachable.
              opacity: starShown ? 1 : 0,
              transition: "opacity 0.15s ease",
            }}
          >
            {isPinned ? "★" : "☆"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        width: isCollapsed ? 70 : 220,
        padding: isCollapsed ? "16px 8px" : 16,
        borderRight: "1px solid #e5e7eb",
        background: "#fafafa",
        height: "calc(100vh - 64px)",
        position: "fixed",
        top: 64,
        left: 0,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s ease",
        zIndex: 999,
        overflowY: "auto",
      }}
    >
      <div>
        {/* Header with collapse button */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: isCollapsed ? "center" : "space-between",
          marginBottom: 12
        }}>
          {!isCollapsed && (
            <div>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>ClientZone</div>
              <div style={{ color: "#6b7280", fontSize: 12 }}>
                Simple Billing & Time
              </div>
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{
              background: "transparent",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#e5e7eb";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? "»" : "«"}
          </button>
        </div>

        {/* Navigation */}
        <nav style={{ marginTop: 16 }}>
          {pins.length > 0 && (
            <div
              data-testid="nav-section-pinned"
              style={{ display: "grid", gap: 6, marginBottom: 14 }}
            >
              {!isCollapsed && (
                <div data-testid="nav-heading-pinned" style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                  textTransform: "uppercase", color: "#9ca3af",
                  padding: "0 12px",
                }}>
                  Pinned
                </div>
              )}
              {/* Nav order, not pin order. The spec's rule is that pins are
                  a set "rendered in the nav's own order"; mapping over `pins`
                  rendered them in the order they were added, so a pin that
                  survived a failed unpin -- which appends it back -- silently
                  jumped to the end of the section. */}
              {visible
                .filter((item) => pins.includes(item.id))
                .map((item) => renderItem(item))}
            </div>
          )}
          {SECTIONS.map((section) => {
            const items = visible.filter(
              (i) => i.section === section.key && !pins.includes(i.id),
            );
            if (!items.length) return null;
            return (
              <div
                key={section.key}
                data-testid={`nav-section-${section.key}`}
                style={{
                  display: "grid", gap: 6, marginBottom: 14,
                  // Collapsed, the headings are gone, so a rule is what keeps
                  // the grouping legible as rhythm.
                  borderTop: isCollapsed ? "1px solid #e5e7eb" : "none",
                  paddingTop: isCollapsed ? 10 : 0,
                }}
              >
                {!isCollapsed && (
                  <div
                    data-testid={`nav-heading-${section.key}`}
                    style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                      textTransform: "uppercase", color: "#9ca3af",
                      padding: "0 12px",
                    }}
                  >
                    {section.label}
                  </div>
                )}
                {items.map((item) => renderItem(item))}
              </div>
            );
          })}
        </nav>
        {/* A live region has to be in the DOM before its content changes for
            a screen reader to announce it, so this container renders empty
            rather than mounting with the message. Without it, a user who
            clicks a ninth star hears nothing at all. */}
        {!isCollapsed && (
          <div
            aria-live="polite"
            data-testid="nav-pin-error"
            style={{ color: "#b91c1c", fontSize: 11, padding: "0 12px" }}
          >
            {pinError}
          </div>
        )}
      </div>

      {/* Settings at bottom */}
      <div style={{ 
        marginTop: "auto", 
        borderTop: "1px solid #e5e7eb", 
        paddingTop: 12 
      }}>
        {canManage(user) && (
          <NavLink
            to="/settings"
            style={({ isActive }) => ({
              ...linkStyle,
              ...(isActive ? activeStyle : {}),
              justifyContent: isCollapsed ? "center" : "flex-start",
            })}
            title={isCollapsed ? "Settings" : ""}
          >
            <span style={iconStyle}>⚙️</span>
            {!isCollapsed && <span>Settings</span>}
          </NavLink>
        )}
      </div>
    </div>
  );
}